import { navigationPlatformApi, navigationReducer as platformNavigationReducer } from 'react-common'
import { Action, combineReducers, configureStore, ThunkAction } from '@reduxjs/toolkit'
import { PersistConfig, persistReducer, persistStore } from 'redux-persist'
import storage from 'redux-persist/lib/storage/session'
import { objectiveKnowledgeModelsApi } from '@/features/km/objectiveKnowledgeModelsApi'
import { activityFinishListenerMiddleware } from '@/store/listenerMiddlewares/activityFinishListenerMiddleware'
import { refetchActivityAccordingConstraintMiddleware } from '@/store/listenerMiddlewares/refetchAccordingConstraintMiddleware/refetchActivityAccordingConstraintMiddleware'
import { refetchTreeAccordingConstraintMiddleware } from '@/store/listenerMiddlewares/refetchAccordingConstraintMiddleware/refetchTreeAccordingConstraintMiddleware'
import { aggregatesApi } from './api/aggregatesApi'
import { certificateRecordApi } from './api/certificateRecordApi'
import { collaborativeApi } from './api/collaborativeApi'
import { objectiveWorkflowApi } from './api/objectiveWorkflowApi'
import { proctoringSessionApi } from './api/proctoringSessionApi'
import { publicCatalogApi } from './api/publicCatalogApi'
import { showcasesApi } from './api/showcasesApi'
import { termsApi } from './api/termsApi'
import { activityStartListenerMiddleware } from './listenerMiddlewares/activityStartListenerMiddleware'
import { aggregateListenerMiddleware } from './listenerMiddlewares/aggregateListenerMiddleware/aggregateListenerMiddleware'
import { aggregateSetActiveActivityPartAxisMiddleware } from './listenerMiddlewares/aggregateSetActiveActivityMiddleware/aggregateSetActiveActivityPartAxisMiddleware'
import activityRecordsReducer from './slices/activityRecordsSlice'
import aggregateReducer from './slices/aggregateSlice'
import aggregatesReducer, { AggregatesSliceState } from './slices/aggregatesSlice'
import layoutReducer from './slices/layoutSlice'
import loadingReducer from './slices/loadingSlice'
import objectiveRecordsReducer from './slices/objectiveRecordsSlice'
import scrollToTopReducer from './slices/scrollToTopSlice'
import contentHeaderPluginReducer from '../features/contentHeaderPlugin/contentHeaderPluginSlice'
import sidebarPluginReducer from '../features/sidebarPlugin/sidebarPluginSlice'
import tocMenuReducer from '../features/tocMenu/tocMenuSlice'

const aggregatesPersistConfig: PersistConfig<AggregatesSliceState> = {
  key: 'aggregates',
  storage,
  whitelist: ['filters', 'catalogCoursesSearchText', 'enrolledCoursesSearchText'],
}

const rootReducer = combineReducers({
  loading: loadingReducer,
  aggregates: persistReducer<AggregatesSliceState>(aggregatesPersistConfig, aggregatesReducer),
  aggregate: aggregateReducer, // TODO combine aggergate and aggregates
  activityRecords: activityRecordsReducer,
  objectiveRecords: objectiveRecordsReducer,
  layout: layoutReducer,
  tocMenu: tocMenuReducer,
  scrollToTop: scrollToTopReducer,
  contentHeaderPlugin: contentHeaderPluginReducer,
  sidebarPlugin: sidebarPluginReducer,
  [showcasesApi.reducerPath]: showcasesApi.reducer,
  [publicCatalogApi.reducerPath]: publicCatalogApi.reducer,
  [certificateRecordApi.reducerPath]: certificateRecordApi.reducer,
  [objectiveWorkflowApi.reducerPath]: objectiveWorkflowApi.reducer,
  [termsApi.reducerPath]: termsApi.reducer,
  [collaborativeApi.reducerPath]: collaborativeApi.reducer,
  [aggregatesApi.reducerPath]: aggregatesApi.reducer,
  [proctoringSessionApi.reducerPath]: proctoringSessionApi.reducer,
  objectiveKnowledgeModelsApi: objectiveKnowledgeModelsApi.reducer,
  ...platformNavigationReducer,
})

export function setupStore(preloadedState?: Partial<RootState>) {
  return configureStore({
    devTools: process.env.NODE_ENV !== 'production',
    reducer: rootReducer,
    middleware: (getDefaultMiddleWare) =>
      getDefaultMiddleWare({ serializableCheck: false })
        .prepend([
          aggregateListenerMiddleware.middleware,
          aggregateSetActiveActivityPartAxisMiddleware,
          activityFinishListenerMiddleware.middleware,
          activityStartListenerMiddleware.middleware,
        ] as const)
        .concat([
          navigationPlatformApi.middleware,
          showcasesApi.middleware,
          publicCatalogApi.middleware,
          certificateRecordApi.middleware,
          objectiveWorkflowApi.middleware,
          refetchTreeAccordingConstraintMiddleware.middleware,
          refetchActivityAccordingConstraintMiddleware.middleware,
          termsApi.middleware,
          collaborativeApi.middleware,
          aggregatesApi.middleware,
          proctoringSessionApi.middleware,
          objectiveKnowledgeModelsApi.middleware,
        ] as const),
    preloadedState,
  })
}

export const store = setupStore()

export const persistor = persistStore(store)

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof rootReducer>
export type AppStore = ReturnType<typeof setupStore>
export type AppDispatch = typeof store.dispatch

export type AppThunk<ReturnType = void> = ThunkAction<ReturnType, RootState, unknown, Action<string>>
